import { SalaryInputs, CalculationResult, PeriodBreakdown, TaxBandBreakdown, StudentLoanPlan } from '../types/calculator';

/**
 * Standard UK Tax Engine for 2024/25 & 2025/26
 */

export function parseTaxCode(taxCodeRaw: string, isBlind: boolean, marriageAllowance: 'none' | 'transfer_out' | 'receive'): {
  personalAllowance: number;
  isScottish: boolean;
  isWelsh: boolean;
  isSpecialCode: boolean;
  specialType?: string;
  kCodeAddition?: number;
} {
  const code = (taxCodeRaw || '1257L').trim().toUpperCase();
  const isScottish = code.startsWith('S');
  const isWelsh = code.startsWith('C');
  const cleanCode = isScottish || isWelsh ? code.slice(1) : code;

  let baseAllowance = 12570;

  // Blind Allowance
  if (isBlind) {
    baseAllowance += 3070;
  }

  // Marriage Allowance adjustment
  if (marriageAllowance === 'transfer_out') {
    baseAllowance -= 1260;
  } else if (marriageAllowance === 'receive') {
    baseAllowance += 1260;
  }

  // Special tax codes
  if (cleanCode === 'BR') {
    return { personalAllowance: 0, isScottish, isWelsh, isSpecialCode: true, specialType: 'BR' };
  }
  if (cleanCode === 'D0') {
    return { personalAllowance: 0, isScottish, isWelsh, isSpecialCode: true, specialType: 'D0' };
  }
  if (cleanCode === 'D1') {
    return { personalAllowance: 0, isScottish, isWelsh, isSpecialCode: true, specialType: 'D1' };
  }
  if (cleanCode === 'NT') {
    return { personalAllowance: 0, isScottish, isWelsh, isSpecialCode: true, specialType: 'NT' };
  }
  if (cleanCode === '0T') {
    return { personalAllowance: 0, isScottish, isWelsh, isSpecialCode: false };
  }

  // Handle K codes (e.g., K1000 means add £10,000 to taxable income)
  if (cleanCode.startsWith('K')) {
    const num = parseInt(cleanCode.replace(/[^0-9]/g, ''), 10);
    const addition = isNaN(num) ? 0 : num * 10;
    return { personalAllowance: 0, isScottish, isWelsh, isSpecialCode: false, kCodeAddition: addition };
  }

  // Handle standard numeric codes (e.g., 1257L, 1300M, 1100N, 1257L W1/M1)
  const numMatch = cleanCode.match(/^(\d+)/);
  if (numMatch) {
    const num = parseInt(numMatch[1], 10);
    baseAllowance = num * 10;
  }

  return { personalAllowance: Math.max(0, baseAllowance), isScottish, isWelsh, isSpecialCode: false };
}

export function calculateUKTax(inputs: SalaryInputs): CalculationResult {
  const {
    grossIncome,
    incomePeriod,
    hoursPerWeek = 37.5,
    daysPerWeek = 5,
    region,
    taxCode,
    isBlind,
    marriageAllowance,
    pensionContribution,
    pensionType,
    pensionMethod,
    studentLoanPlan,
    niCategory,
    bonus = 0,
    overtimeHoursPerMonth = 0,
    overtimeHourlyRate = 0,
    benefitInKind = 0,
  } = inputs;

  // 1. Calculate Annual Base Gross Salary
  let annualGrossBase = 0;
  switch (incomePeriod) {
    case 'year':
      annualGrossBase = grossIncome;
      break;
    case 'month':
      annualGrossBase = grossIncome * 12;
      break;
    case '4week':
      annualGrossBase = grossIncome * 13;
      break;
    case '2week':
      annualGrossBase = grossIncome * 26;
      break;
    case 'week':
      annualGrossBase = grossIncome * 52;
      break;
    case 'day':
      annualGrossBase = grossIncome * daysPerWeek * 52;
      break;
    case 'hour':
      annualGrossBase = grossIncome * hoursPerWeek * 52;
      break;
  }

  // Add Bonus and Overtime
  const annualOvertime = overtimeHoursPerMonth * overtimeHourlyRate * 12;
  const annualGross = annualGrossBase + bonus + annualOvertime;

  // 2. Parse Tax Code
  const parsedCode = parseTaxCode(taxCode, isBlind, marriageAllowance);
  const isScottish = region === 'scotland' || parsedCode.isScottish;

  // 3. Pension Calculation
  let annualPension = 0;
  if (pensionContribution > 0) {
    if (pensionType === 'percentage') {
      annualPension = (annualGross * pensionContribution) / 100;
    } else {
      annualPension = pensionContribution; // fixed annual
    }
  }

  // Adjusted Gross after Salary Sacrifice Pension
  let grossAfterSacrifice = annualGross;
  if (pensionMethod === 'salary_sacrifice') {
    grossAfterSacrifice = Math.max(0, annualGross - annualPension);
  }

  // Benefit in kind increases taxable salary for income tax purposes
  const taxableGrossForIncomeTax = grossAfterSacrifice + benefitInKind;

  // Net Pay Pension / Salary Sacrifice reduce income taxable gross
  let incomeTaxableGross = taxableGrossForIncomeTax;
  if (pensionMethod === 'net_pay') {
    incomeTaxableGross = Math.max(0, taxableGrossForIncomeTax - annualPension);
  }

  // 4. Personal Allowance Calculation & Tapering
  let personalAllowance = parsedCode.personalAllowance;
  let kCodeAddition = parsedCode.kCodeAddition || 0;

  // Personal Allowance Taper (£1 reduced for every £2 above £100,000)
  if (!parsedCode.isSpecialCode && personalAllowance > 0 && incomeTaxableGross > 100000) {
    const excess = incomeTaxableGross - 100000;
    const reduction = Math.floor(excess / 2);
    personalAllowance = Math.max(0, personalAllowance - reduction);
  }

  // 5. Calculate Income Tax
  let totalIncomeTax = 0;
  const taxBands: TaxBandBreakdown[] = [];

  if (parsedCode.isSpecialCode) {
    if (parsedCode.specialType === 'BR') {
      const tax = incomeTaxableGross * 0.20;
      totalIncomeTax = tax;
      taxBands.push({ name: 'Basic Rate (20%)', rate: 20, taxableAmount: incomeTaxableGross, taxCharged: tax });
    } else if (parsedCode.specialType === 'D0') {
      const tax = incomeTaxableGross * 0.40;
      totalIncomeTax = tax;
      taxBands.push({ name: 'Higher Rate (40%)', rate: 40, taxableAmount: incomeTaxableGross, taxCharged: tax });
    } else if (parsedCode.specialType === 'D1') {
      const tax = incomeTaxableGross * 0.45;
      totalIncomeTax = tax;
      taxBands.push({ name: 'Additional Rate (45%)', rate: 45, taxableAmount: incomeTaxableGross, taxCharged: tax });
    } else if (parsedCode.specialType === 'NT') {
      totalIncomeTax = 0;
      taxBands.push({ name: 'No Tax (0%)', rate: 0, taxableAmount: incomeTaxableGross, taxCharged: 0 });
    }
  } else {
    // Standard Tax Band Calculation
    const effectiveTaxableIncome = Math.max(0, incomeTaxableGross - personalAllowance + kCodeAddition);

    if (isScottish) {
      // Scottish Tax Bands 2024/25 & 2025/26
      // Bands above PA:
      // Starter: £0 - £2,306 (19%)
      // Basic: £2,307 - £13,991 (20%)
      // Intermediate: £13,992 - £31,092 (21%)
      // Higher: £31,093 - £62,430 (42%)
      // Advanced: £62,431 - £112,570 (45%)
      // Top: > £112,570 (48%)
      const scottishBands = [
        { name: 'Starter Rate (19%)', rate: 0.19, cap: 2306 },
        { name: 'Basic Rate (20%)', rate: 0.20, cap: 11685 },
        { name: 'Intermediate Rate (21%)', rate: 0.21, cap: 17101 },
        { name: 'Higher Rate (42%)', rate: 0.42, cap: 31338 },
        { name: 'Advanced Rate (45%)', rate: 0.45, cap: 50140 },
        { name: 'Top Rate (48%)', rate: 0.48, cap: Infinity },
      ];

      let remainingTaxable = effectiveTaxableIncome;
      for (const band of scottishBands) {
        if (remainingTaxable <= 0) break;
        const amountInBand = Math.min(remainingTaxable, band.cap);
        const tax = amountInBand * band.rate;
        totalIncomeTax += tax;
        taxBands.push({
          name: band.name,
          rate: band.rate * 100,
          taxableAmount: amountInBand,
          taxCharged: tax,
        });
        remainingTaxable -= amountInBand;
      }
    } else {
      // England, Wales, Northern Ireland Tax Bands
      // Basic Rate (20%): £0 - £37,700 above PA
      // Higher Rate (40%): £37,701 - £112,570 above PA
      // Additional Rate (45%): > £112,570 above PA
      const rUKBands = [
        { name: 'Basic Rate (20%)', rate: 0.20, cap: 37700 },
        { name: 'Higher Rate (40%)', rate: 0.40, cap: 74870 }, // 112,570 - 37,700 = 74,870
        { name: 'Additional Rate (45%)', rate: 0.45, cap: Infinity },
      ];

      let remainingTaxable = effectiveTaxableIncome;
      for (const band of rUKBands) {
        if (remainingTaxable <= 0) break;
        const amountInBand = Math.min(remainingTaxable, band.cap);
        const tax = amountInBand * band.rate;
        totalIncomeTax += tax;
        taxBands.push({
          name: band.name,
          rate: band.rate * 100,
          taxableAmount: amountInBand,
          taxCharged: tax,
        });
        remainingTaxable -= amountInBand;
      }
    }
  }

  // 6. Calculate National Insurance (Employee Class 1)
  // NI is calculated on Gross after Salary Sacrifice (NI threshold = £12,570 / yr, UEL = £50,270 / yr)
  let totalNI = 0;
  const niBands: TaxBandBreakdown[] = [];
  const niThreshold = 12570;
  const niUpperLimit = 50270;

  let mainNiRate = 0.08; // 8% standard Cat A
  if (niCategory === 'B') mainNiRate = 0.0185;
  if (niCategory === 'C') mainNiRate = 0; // State pension age
  if (['H', 'M', 'V'].includes(niCategory)) mainNiRate = 0; // Under 21 / apprentices

  const higherNiRate = niCategory === 'C' ? 0 : 0.02; // 2% above £50,270

  const niTaxableGross = Math.max(0, grossAfterSacrifice);

  if (niTaxableGross > niThreshold) {
    const mainBandAmount = Math.min(niTaxableGross - niThreshold, niUpperLimit - niThreshold);
    const mainNi = mainBandAmount * mainNiRate;
    totalNI += mainNi;
    if (mainNiRate > 0) {
      niBands.push({
        name: `Main NI Rate (${(mainNiRate * 100).toFixed(2)}%)`,
        rate: mainNiRate * 100,
        taxableAmount: mainBandAmount,
        taxCharged: mainNi,
      });
    }

    if (niTaxableGross > niUpperLimit) {
      const higherBandAmount = niTaxableGross - niUpperLimit;
      const higherNi = higherBandAmount * higherNiRate;
      totalNI += higherNi;
      niBands.push({
        name: `Higher NI Rate (${(higherNiRate * 100).toFixed(0)}%)`,
        rate: higherNiRate * 100,
        taxableAmount: higherBandAmount,
        taxCharged: higherNi,
      });
    }
  }

  // 7. Student Loan Repayments
  let annualStudentLoan = 0;
  const slGross = grossAfterSacrifice;

  const calculatePlan = (plan: string): number => {
    switch (plan) {
      case 'plan1':
        return slGross > 24990 ? (slGross - 24990) * 0.09 : 0;
      case 'plan2':
        return slGross > 27295 ? (slGross - 27295) * 0.09 : 0;
      case 'plan4':
        return slGross > 31395 ? (slGross - 31395) * 0.09 : 0;
      case 'plan5':
        return slGross > 25000 ? (slGross - 25000) * 0.09 : 0;
      case 'postgrad':
        return slGross > 21000 ? (slGross - 21000) * 0.06 : 0;
      default:
        return 0;
    }
  };

  if (studentLoanPlan.includes('_postgrad')) {
    const ugPlan = studentLoanPlan.replace('_postgrad', '');
    annualStudentLoan = calculatePlan(ugPlan) + calculatePlan('postgrad');
  } else if (studentLoanPlan !== 'none') {
    annualStudentLoan = calculatePlan(studentLoanPlan);
  }

  // 8. Total Deductions & Net Take Home Pay
  // Total deductions includes Income Tax + NI + Pension (if paid out of gross/net) + Student Loan
  const totalDeductions = totalIncomeTax + totalNI + annualPension + annualStudentLoan;
  const annualNetPay = Math.max(0, annualGross - totalDeductions);

  const effectiveTaxRate = annualGross > 0 ? ((totalIncomeTax + totalNI) / annualGross) * 100 : 0;

  // Calculate Marginal Tax Rate
  let marginalTaxRate = 0;
  if (annualGross > 125140) {
    marginalTaxRate = isScottish ? 48 + 2 : 45 + 2;
  } else if (annualGross > 100000) {
    // In £100k-£125.14k, PA is tapered so effective marginal tax rate is +20% higher tax + NI
    marginalTaxRate = isScottish ? 42 + 20 + 2 : 40 + 20 + 2; // 64% in Scotland or 62% in rUK
  } else if (annualGross > 50270) {
    marginalTaxRate = isScottish ? 42 + 2 : 40 + 2;
  } else if (annualGross > 12570) {
    marginalTaxRate = isScottish ? 20 + 8 : 20 + 8;
  }

  // Helper to construct period breakdown
  const buildPeriod = (divisor: number): PeriodBreakdown => {
    const gross = annualGross / divisor;
    const taxableGross = incomeTaxableGross / divisor;
    const tax = totalIncomeTax / divisor;
    const ni = totalNI / divisor;
    const pension = annualPension / divisor;
    const sl = annualStudentLoan / divisor;
    const totalDed = totalDeductions / divisor;
    const net = annualNetPay / divisor;

    return {
      gross,
      taxableGross,
      incomeTax: tax,
      nationalInsurance: ni,
      pension,
      studentLoan: sl,
      totalDeductions: totalDed,
      netPay: net,
    };
  };

  const totalWorkingDays = daysPerWeek * 52;
  const totalWorkingHours = hoursPerWeek * 52;

  const breakdowns = {
    yearly: buildPeriod(1),
    monthly: buildPeriod(12),
    fourWeekly: buildPeriod(13),
    fortnightly: buildPeriod(26),
    weekly: buildPeriod(52),
    daily: buildPeriod(totalWorkingDays > 0 ? totalWorkingDays : 260),
    hourly: buildPeriod(totalWorkingHours > 0 ? totalWorkingHours : 1950),
  };

  return {
    inputs,
    annualGross,
    annualTaxableGross: incomeTaxableGross,
    personalAllowance,
    incomeTax: totalIncomeTax,
    nationalInsurance: totalNI,
    pensionContribution: annualPension,
    studentLoanDeduction: annualStudentLoan,
    totalDeductions,
    annualNetPay,
    effectiveTaxRate,
    marginalTaxRate,
    taxBands,
    niBands,
    breakdowns,
  };
}

/**
 * Reverse Tax Engine: Calculate Gross required for target Net income
 */
export function calculateGrossFromNet(
  targetNet: number,
  period: 'year' | 'month',
  baseInputs: SalaryInputs
): number {
  const targetAnnualNet = period === 'month' ? targetNet * 12 : targetNet;

  let low = targetAnnualNet;
  let high = targetAnnualNet * 3;
  let grossGuess = targetAnnualNet * 1.4;

  for (let i = 0; i < 25; i++) {
    const testResult = calculateUKTax({
      ...baseInputs,
      grossIncome: grossGuess,
      incomePeriod: 'year',
    });

    const diff = testResult.annualNetPay - targetAnnualNet;
    if (Math.abs(diff) < 0.5) {
      break;
    }

    if (testResult.annualNetPay < targetAnnualNet) {
      low = grossGuess;
    } else {
      high = grossGuess;
    }
    grossGuess = (low + high) / 2;
  }

  return Math.round(grossGuess);
}
